import { isPhoneLinked } from '@/lib/phone-connection';
import { api } from '@/lib/api';
import { track, trackOnce } from '@/lib/analytics';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { storage } from '@/lib/utils';
import { config } from '@/lib/config';
import { qk, queryClient } from './query';
import { usePhoneSyncProgress } from './phone-sync-progress';
import { useEmployees, useWorkspace } from './workspace';

export interface SetupStep {
  id: 'intro' | 'employee' | 'first_sync' | 'recording' | 'agent';
  title: string;
  description: string;
  done: boolean;
  href: string;
  cta: string;
}

/**
 * Setup milestones for the selected person: linked phone, first calls, optional recordings and AI.
 * `fastPoll` shortens call/recording checks while a setup screen is waiting for them.
 */
export function useSetupProgress(
  employeeId?: number,
  pollEmployees = true,
  pollMilestones = true,
  fastPoll = false,
) {
  const { orgId, org, user } = useWorkspace();
  const completed = org.onboardingCompleted;
  const employees = useEmployees(pollEmployees);
  const integrationPreference = useQuery({
    queryKey: ['setupIntegration', orgId, user.id],
    queryFn: () => storage.get(`cs_setup_chatgpt_${orgId}_${user.id}`, false),
    staleTime: Infinity,
  });
  const appsAvailable = !config.isSandbox || config.oauthServerEnabled;
  const connectedApps = useQuery({
    queryKey: qk.connectedApps(user.id),
    queryFn: () => api.integrations.listConnectedApps(),
    enabled: appsAvailable,
    refetchInterval: pollMilestones && !completed ? 30_000 : false,
  });
  const agentConnected = Boolean(connectedApps.data?.length);
  const agentSkipped = integrationPreference.data === true;

  const preferenceScope = employeeId === undefined ? orgId : `${orgId}_${employeeId}`;
  const preferenceKey = ['setupPreferences', orgId, employeeId ?? 'all'] as const;
  const preferences = useQuery({
    queryKey: preferenceKey,
    queryFn: () =>
      storage.get(`cs_setup_preferences_${preferenceScope}`, { introDone: false, recordingSkipped: false }),
    staleTime: Infinity,
  });
  const updatePreferences = (patch: Partial<{ introDone: boolean; recordingSkipped: boolean }>) => {
    const next = { introDone: false, recordingSkipped: false, ...preferences.data, ...patch };
    storage.set(`cs_setup_preferences_${preferenceScope}`, next);
    queryClient.setQueryData(preferenceKey, next);
  };
  const list = employees.data ?? [];
  const hasPeople = list.length > 0;
  const { hasCalls, hasRecordings } = usePhoneSyncProgress(
    employeeId,
    hasPeople,
    pollMilestones && !completed,
    fastPoll ? 10_000 : 30_000,
  );
  const phoneConnected = list.some(
    (e) => (employeeId === undefined || e.id === employeeId) && isPhoneLinked(e),
  );
  const recordingSkipped = preferences.data?.recordingSkipped ?? false;
  const scope = `${orgId}:${employeeId ?? 'all'}`;
  const [resolvedScope, setResolvedScope] = useState<string | null>(null);
  // Resolve only the checks needed to find the next unfinished milestone.
  const checkingPhone = hasPeople && phoneConnected;
  const checkingRecordings = checkingPhone && Boolean(hasCalls.data) && !recordingSkipped;
  const checkingAgent =
    checkingPhone && Boolean(hasCalls.data) && (recordingSkipped || Boolean(hasRecordings.data));
  const navigationPending =
    !completed &&
    (employees.isPending ||
      preferences.isPending ||
      (checkingPhone && hasCalls.isPending) ||
      (checkingRecordings && hasRecordings.isPending) ||
      (checkingAgent &&
        (integrationPreference.isPending || (appsAvailable && !agentSkipped && connectedApps.isPending))));
  useEffect(() => {
    if (!navigationPending) setResolvedScope(scope);
  }, [navigationPending, scope]);
  const steps: SetupStep[] = [
    {
      id: 'intro',
      title: 'How it works',
      description: 'See how phone calls and recordings reach your dashboard.',
      done: Boolean(preferences.data?.introDone) || list.length > 0,
      href: '/welcome?step=intro',
      cta: 'See how it works',
    },
    {
      id: 'employee',
      title: 'Add a person and install',
      description: 'Add yourself or a team member, then install CallSync on their Android phone or iPhone.',
      done: list.length > 0 && phoneConnected,
      href: list.length ? '/welcome?step=install' : '/welcome?step=person',
      cta: list.length ? 'Install on the phone' : 'Add a person',
    },
    {
      id: 'first_sync',
      title: 'Link and sync',
      description:
        'Link the phone in CallSync and tap Start syncing. Calls from the chosen dates appear here.',
      done: Boolean(hasCalls.data),
      href: '/welcome?step=link',
      cta: 'Link and sync',
    },
    {
      id: 'recording',
      title: 'Recordings (optional)',
      description: 'Upload recordings saved by the phone’s calling app, or skip this.',
      done: Boolean(hasRecordings.data) || recordingSkipped,
      href: '/welcome?step=recordings',
      cta: 'Set up recordings',
    },
    {
      id: 'agent',
      title: 'Connect an AI agent (optional)',
      description:
        'Use your call history with ChatGPT, Claude or another compatible AI agent, or connect later.',
      done: agentConnected || agentSkipped,
      href: '/welcome?step=agent',
      cta: 'Connect AI agent',
    },
  ];
  const milestonesComplete = steps.every((s) => s.done);
  if (completed)
    steps.forEach((s) => {
      s.done = true;
    });
  const done = steps.filter((s) => s.done).length;
  return {
    steps,
    finish: async () => {
      const savedOrg = await api.org.completeOnboarding(orgId);
      if (!completed && savedOrg.onboardingCompleted)
        trackOnce(`onboarding-complete:${orgId}`, 'tutorial_complete', {
          recording_skipped: recordingSkipped,
          agent_skipped: !agentConnected,
        });
      queryClient.setQueryData(qk.org(user.id), savedOrg);
    },
    agentConnected,
    agentSkipped,
    connectedApps,
    skipAgent: () => {
      track('onboarding_step_skipped', { step: 'agent' });
      storage.set(`cs_setup_chatgpt_${orgId}_${user.id}`, true);
      queryClient.setQueryData(['setupIntegration', orgId, user.id], true);
    },
    hasCalls,
    hasRecordings,
    recordingSkipped,
    acknowledgeIntro: () => {
      track('onboarding_step_completed', { step: 'intro' });
      updatePreferences({ introDone: true });
    },
    skipRecording: () => {
      track('onboarding_step_skipped', { step: 'recordings' });
      updatePreferences({ recordingSkipped: true });
    },
    error:
      !completed &&
      (employees.error ||
        (hasPeople && (hasCalls.error || (hasCalls.data && !recordingSkipped && hasRecordings.error)))),
    retry: () =>
      Promise.all([
        employees.refetch(),
        ...(hasPeople ? [hasCalls.refetch()] : []),
        ...(hasPeople && hasCalls.data ? [hasRecordings.refetch()] : []),
        ...(appsAvailable ? [connectedApps.refetch()] : []),
      ]),
    done,
    total: steps.length,
    complete: completed || milestonesComplete,
    // First-call polling can enable the recording query later. Keep the already
    // visible workspace mounted while that background milestone is checked.
    loading: navigationPending && resolvedScope !== scope,
    next: steps.find((s) => !s.done) ?? null,
  };
}
